import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { 
  CogIcon, 
  MapPinIcon,
  ClockIcon,
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

function AppSettings() {
  const [settings, setSettings] = useState({
    churchAddress: {
      name: 'Community Church',
      street: '1093 Meyerside Dr',
      city: 'Mississauga',
      province: 'ON',
      postalCode: 'L5T 1J6',
      country: 'Canada',
      phone: '',
      email: '',
      website: ''
    },
    services: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: '',
    day: 'Sunday',
    time: '',
    description: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API endpoints when backend is ready
      // For now, using default values
      setSettings({
        churchAddress: {
          name: 'Community Church',
          street: '1093 Meyerside Dr',
          city: 'Mississauga',
          province: 'ON',
          postalCode: 'L5T 1J6',
          country: 'Canada',
          phone: '(905) 123-4567',
          email: 'info@communitychurch.org',
          website: 'www.communitychurch.org'
        },
        services: [
          {
            id: 1,
            name: 'Morning Worship',
            day: 'Sunday',
            time: '10:00',
            description: 'Traditional worship service'
          },
          {
            id: 2,
            name: 'Evening Service',
            day: 'Sunday',
            time: '18:00',
            description: 'Contemporary worship service'
          },
          {
            id: 3,
            name: 'Wednesday Prayer',
            day: 'Wednesday',
            time: '19:00',
            description: 'Midweek prayer meeting'
          }
        ]
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      churchAddress: {
        ...prev.churchAddress,
        [field]: value
      }
    }));
  };

  const saveAddress = async () => {
    try {
      setSaving(true);
      setError('');
      
      // TODO: Implement API call to save church address
      // await axiosInstance.put(API_URLS.admin.settings.churchAddress, settings.churchAddress);
      
      setSuccess('Church address updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving address:', err);
      setError('Failed to save church address');
    } finally {
      setSaving(false);
    }
  };

  const handleServiceChange = (field, value) => {
    setNewService(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addService = async () => {
    try {
      if (!newService.name || !newService.time) {
        setError('Please fill in all required fields');
        return;
      }

      setSaving(true);
      setError('');

      const serviceToAdd = {
        ...newService,
        id: Date.now() // Temporary ID generation
      };

      setSettings(prev => ({
        ...prev,
        services: [...prev.services, serviceToAdd]
      }));

      setNewService({
        name: '',
        day: 'Sunday',
        time: '',
        description: ''
      });

      // TODO: Implement API call to add service
      // await axiosInstance.post(API_URLS.admin.settings.services, serviceToAdd);

      setSuccess('Service added successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding service:', err);
      setError('Failed to add service');
    } finally {
      setSaving(false);
    }
  };

  const editService = (service) => {
    setEditingService({ ...service });
  };

  const saveServiceEdit = async () => {
    try {
      setSaving(true);
      setError('');

      setSettings(prev => ({
        ...prev,
        services: prev.services.map(service => 
          service.id === editingService.id ? editingService : service
        )
      }));

      setEditingService(null);

      // TODO: Implement API call to update service
      // await axiosInstance.put(`${API_URLS.admin.settings.services}/${editingService.id}`, editingService);

      setSuccess('Service updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating service:', err);
      setError('Failed to update service');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (serviceId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this service?')) {
        return;
      }

      setSaving(true);
      setError('');

      setSettings(prev => ({
        ...prev,
        services: prev.services.filter(service => service.id !== serviceId)
      }));

      // TODO: Implement API call to delete service
      // await axiosInstance.delete(`${API_URLS.admin.settings.services}/${serviceId}`);

      setSuccess('Service deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting service:', err);
      setError('Failed to delete service');
    } finally {
      setSaving(false);
    }
  };

  const daysOfWeek = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 
    'Thursday', 'Friday', 'Saturday'
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">App Settings</h1>
          <p className="mt-2 text-sm text-gray-700">
            Configure church information and service schedules
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Church Address Section */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-6">
              <BuildingOfficeIcon className="h-8 w-8 text-indigo-600 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Church Address</h2>
                <p className="text-sm text-gray-500">Update your church's contact information and address</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="churchName" className="block text-sm font-medium text-gray-700">
                  Church Name
                </label>
                <input
                  type="text"
                  id="churchName"
                  value={settings.churchAddress.name}
                  onChange={(e) => handleAddressChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                  Street Address
                </label>
                <input
                  type="text"
                  id="street"
                  value={settings.churchAddress.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={settings.churchAddress.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="province" className="block text-sm font-medium text-gray-700">
                  Province/State
                </label>
                <input
                  type="text"
                  id="province"
                  value={settings.churchAddress.province}
                  onChange={(e) => handleAddressChange('province', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  id="postalCode"
                  value={settings.churchAddress.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  value={settings.churchAddress.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    value={settings.churchAddress.phone}
                    onChange={(e) => handleAddressChange('phone', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={settings.churchAddress.email}
                    onChange={(e) => handleAddressChange('email', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="info@church.org"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                  Website
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    id="website"
                    value={settings.churchAddress.website}
                    onChange={(e) => handleAddressChange('website', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="www.church.org"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={saveAddress}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Address'}
              </button>
            </div>
          </div>
        </div>

        {/* Services & Time Section */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <CalendarIcon className="h-8 w-8 text-indigo-600 mr-3" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Services & Time</h2>
                  <p className="text-sm text-gray-500">Manage church service schedules</p>
                </div>
              </div>
            </div>

            {/* Add New Service Form */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Service</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                  <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700">
                    Service Name
                  </label>
                  <input
                    type="text"
                    id="serviceName"
                    value={newService.name}
                    onChange={(e) => handleServiceChange('name', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Morning Worship"
                  />
                </div>

                <div>
                  <label htmlFor="serviceDay" className="block text-sm font-medium text-gray-700">
                    Day
                  </label>
                  <select
                    id="serviceDay"
                    value={newService.day}
                    onChange={(e) => handleServiceChange('day', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {daysOfWeek.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="serviceTime" className="block text-sm font-medium text-gray-700">
                    Time
                  </label>
                  <input
                    type="time"
                    id="serviceTime"
                    value={newService.time}
                    onChange={(e) => handleServiceChange('time', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={addService}
                    disabled={saving || !newService.name || !newService.time}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Service
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="serviceDescription" className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <textarea
                  id="serviceDescription"
                  value={newService.description}
                  onChange={(e) => handleServiceChange('description', e.target.value)}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Brief description of the service"
                />
              </div>
            </div>

            {/* Services List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Current Services</h3>
              {settings.services.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No services configured</h3>
                  <p className="mt-1 text-sm text-gray-500">Add your first service to get started.</p>
                </div>
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {settings.services.map((service) => (
                      <li key={service.id}>
                        {editingService && editingService.id === service.id ? (
                          // Edit mode
                          <div className="px-4 py-4 sm:px-6 bg-gray-50">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                              <div>
                                <input
                                  type="text"
                                  value={editingService.name}
                                  onChange={(e) => setEditingService(prev => ({ ...prev, name: e.target.value }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <select
                                  value={editingService.day}
                                  onChange={(e) => setEditingService(prev => ({ ...prev, day: e.target.value }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                  {daysOfWeek.map(day => (
                                    <option key={day} value={day}>{day}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <input
                                  type="time"
                                  value={editingService.time}
                                  onChange={(e) => setEditingService(prev => ({ ...prev, time: e.target.value }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={saveServiceEdit}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingService(null)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-2">
                              <textarea
                                value={editingService.description}
                                onChange={(e) => setEditingService(prev => ({ ...prev, description: e.target.value }))}
                                rows={2}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Description"
                              />
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <ClockIcon className="h-8 w-8 text-indigo-500" />
                                </div>
                                <div className="ml-4">
                                  <h4 className="text-lg font-medium text-gray-900">{service.name}</h4>
                                  <div className="flex items-center mt-1">
                                    <span className="text-sm text-gray-500">{service.day}s at</span>
                                    <span className="ml-1 text-sm font-medium text-gray-900">
                                      {new Date(`2000-01-01 ${service.time}`).toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </span>
                                  </div>
                                  {service.description && (
                                    <p className="mt-1 text-sm text-gray-600">{service.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => editService(service)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => deleteService(service.id)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppSettings; 